import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Header, HeaderButton } from '../../components/header';
import { Route } from '../../@types/routes';
import { AddIcon, BlockIcon, DeleteIcon, MergeIcon, PlayIcon, StopIcon } from '../../assets/svgs';
import { SaveRoutePayload, useResetAllRoutesTeams, useSetRouteInfo } from '../../queries/route';
import { useDeleteRoutes, useMergeRoutes, useSetRoutesActiveState } from '../../queries/routes';
import { ConfirmationModal } from '../../components/confirmation-modal';
import { PromptModal } from '../../components/prompt-modal';
import { AlertModal } from '../../components/alert-modal';
import { useDistribution } from '../../queries/distributions';
import { DistributionNotActiveWarning } from '../../components/not-active-warning';
import { NotesFAB, FAB_BOTTOM_SPACING } from '../../components/notes-fab';
import { RoutesList } from './routes-list';
import { EditRouteModal } from './edit-route-modal';
import { AddRouteModal } from './add-route-modal';
import { NotesModal } from './notes-modal';

export const RoutesEditorScreen = () => {
  const navigate = useNavigate();
  const { distributionId = '' } = useParams();
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [routeToEdit, setRouteToEdit] = useState<Route | null>(null);

  const [isAddRouteModalVisible, setAddRouteModalVisibility] = useState(false);

  const [isConfirmResetTeamsModalVisible, setConfirmResetTeamsModalVisibility] = useState(false);
  const [isConfirmDeleteRoutesModalVisible, setConfirmDeleteRoutesModalVisibility] =
    useState(false);
  const [isPromptMergeRoutesModalVisible, setPromptMergeRoutesModalVisibility] = useState(false);
  const [isAlertMergeRoutesModalVisible, setAlertMergeRoutesModalVisibility] = useState(false);
  const [isAlertActiveRouteSelectedVisible, setAlertActiveRouteSelectedVisibility] =
    useState(false);
  const [isNotesModalVisible, setNotesModalVisibility] = useState(false);

  const {
    isFetching,
    data: distribution,
    refetch: refetchRoutesList,
    isLoading,
  } = useDistribution(distributionId);
  const { mutate: mutateRouteInfo, isLoading: isUpdating } = useSetRouteInfo();
  const { mutate: mutateMergeRoutes, isLoading: isMerging } = useMergeRoutes();
  const { mutate: mutateRoutesActiveState, isLoading: isUpdatingActiveState } =
    useSetRoutesActiveState();
  const { mutate: mutateDeleteRoutes, isLoading: isDeletingRoutes } = useDeleteRoutes();
  const { mutate: mutateResetAllTeams, isLoading: isResetTeams } = useResetAllRoutesTeams();

  const toggleRouteSelect = (route: Route) => {
    const newSelectedRoutes = [...selectedRoutes];

    const itemIndex = newSelectedRoutes.findIndex((selectedItem) => {
      return selectedItem === route.id;
    });

    if (itemIndex >= 0) {
      newSelectedRoutes.splice(itemIndex, 1);
    } else {
      newSelectedRoutes.push(route.id);
    }

    setSelectedRoutes(newSelectedRoutes);
  };

  const handleMergeClick = () => {
    if (selectedRoutes.length < 2) {
      setAlertMergeRoutesModalVisibility(true);

      return;
    }

    setPromptMergeRoutesModalVisibility(true);
  };

  const handleConfirmMergeAlertClick = () => {
    setAlertMergeRoutesModalVisibility(false);
  };

  const handleSaveMergeClick = (newRouteName: string) => {
    setPromptMergeRoutesModalVisibility(false);

    mutateMergeRoutes(
      {
        distributionId,
        newRouteName,
        routesIds: selectedRoutes,
      },
      {
        onSuccess: () => {
          setSelectedRoutes([]);
          refetchRoutesList();
        },
      },
    );
  };

  const changeRoutesActiveState = (newState: boolean) => {
    mutateRoutesActiveState(
      {
        routesIds: selectedRoutes,
        newState: newState,
      },
      {
        onSuccess: () => {
          setSelectedRoutes([]);
          refetchRoutesList();
        },
      },
    );
  };

  const handleSetRoutesActiveClick = () => {
    changeRoutesActiveState(true);
  };

  const handleSetRoutesNotActiveClick = () => {
    changeRoutesActiveState(false);
  };

  const handleDeleteRoutesClick = () => {
    const hasActiveRoute = distribution?.routes
      .filter((route) => selectedRoutes.includes(route.id))
      .some((route) => route.isActive);

    if (hasActiveRoute) {
      setAlertActiveRouteSelectedVisibility(true);

      return;
    }

    setConfirmDeleteRoutesModalVisibility(true);
  };

  const handleConfirmDeleteRoutesClick = () => {
    setConfirmDeleteRoutesModalVisibility(false);

    mutateDeleteRoutes(
      {
        distributionId,
        routesIds: selectedRoutes,
      },
      {
        onSuccess: () => {
          setSelectedRoutes([]);
          refetchRoutesList();
        },
      },
    );
  };

  const handleCancelDeleteRoutesClick = () => {
    setConfirmDeleteRoutesModalVisibility(false);
  };

  const handleCancelMergeClick = () => {
    setPromptMergeRoutesModalVisibility(false);
  };

  const handleEditRouteClick = (route: Route) => {
    setRouteToEdit(route);
  };

  const handleSaveRouteClick = (routePayload: SaveRoutePayload) => {
    setRouteToEdit(null);

    mutateRouteInfo(routePayload, {
      onSuccess: () => {
        refetchRoutesList();
      },
    });
  };

  const handleResetTeamsClick = () => {
    setConfirmResetTeamsModalVisibility(true);
  };

  const handleConfirmResetTeamsClick = () => {
    setConfirmResetTeamsModalVisibility(false);

    mutateResetAllTeams(
      {
        distributionId,
      },
      {
        onSuccess: () => {
          refetchRoutesList();
        },
      },
    );
  };

  const handleCancelResetTeamsClick = () => {
    setConfirmResetTeamsModalVisibility(false);
  };

  const handleCreateRouteClick = () => {
    setAddRouteModalVisibility(true);
  };

  const handleEditNotesClick = () => {
    setNotesModalVisibility(true);
  };

  const renderScreenTitle = () => {
    if (selectedRoutes.length > 0) {
      return `נבחרו: ${selectedRoutes.length}`;
    }

    return 'עריכת מסלולים';
  };

  const renderScreenSubTitle = () => {
    if (selectedRoutes.length <= 0) {
      return '';
    }

    const selectedRoutesObjs =
      distribution?.routes.filter((route) => selectedRoutes.includes(route.id)) || [];

    let totalPackages = 0;
    for (const route of selectedRoutesObjs) {
      totalPackages += route.totalPackages ?? 0;
    }

    return `${totalPackages} אריזות`;
  };

  const renderActionButtons = () => {
    const buttons: HeaderButton[] = [];

    if (selectedRoutes.length > 0) {
      const isAllActive = distribution?.routes
        .filter((route) => selectedRoutes.includes(route.id))
        .every((route) => route.isActive);

      buttons.push({
        title: 'מחיקה',
        icon: <DeleteIcon />,
        onclick: handleDeleteRoutesClick,
      });

      if (isAllActive) {
        buttons.push({
          title: 'השבתת מסלול',
          icon: <StopIcon />,
          onclick: handleSetRoutesNotActiveClick,
        });
      } else {
        buttons.push({
          title: 'הפעלת מסלול',
          icon: <PlayIcon />,
          onclick: handleSetRoutesActiveClick,
        });
      }

      buttons.push({ title: 'איחוד', icon: <MergeIcon />, onclick: handleMergeClick });
    } else {
      buttons.push({ title: 'הוספת מסלול', icon: <AddIcon />, onclick: handleCreateRouteClick });
      buttons.push({ title: 'איפוס צוותים', icon: <BlockIcon />, onclick: handleResetTeamsClick });
    }

    return buttons;
  };

  return (
    <>
      <Header
        screenTitle={renderScreenTitle()}
        screenSubTitle={renderScreenSubTitle()}
        hasBack
        isLoading={
          isFetching ||
          isUpdating ||
          isMerging ||
          isResetTeams ||
          isUpdatingActiveState ||
          isDeletingRoutes
        }
        buttons={renderActionButtons()}
      />
      <div style={{ width: '95%', margin: '10px auto 0 auto', paddingBottom: FAB_BOTTOM_SPACING }}>
        {!isLoading && !distribution?.isActive && <DistributionNotActiveWarning />}

        <NotesFAB onClick={handleEditNotesClick} />

        <RoutesList
          routes={distribution?.routes || []}
          onRouteClick={(route) => {
            navigate(`/distribution/${distributionId}/route/${route.id}`);
          }}
          onToggleRouteSelect={toggleRouteSelect}
          selectedRoutes={selectedRoutes}
          onEditRouteClick={handleEditRouteClick}
        />
      </div>

      {routeToEdit && (
        <EditRouteModal
          route={routeToEdit}
          onSaveClick={handleSaveRouteClick}
          onCancel={() => {
            setRouteToEdit(null);
          }}
        />
      )}

      {isAddRouteModalVisible && (
        <AddRouteModal
          distributionId={distributionId}
          onRouteAdded={() => {
            refetchRoutesList();
            setAddRouteModalVisibility(false);
          }}
          onCancel={() => {
            setAddRouteModalVisibility(false);
          }}
        />
      )}

      <ConfirmationModal
        title="איפוס צוותים"
        isVisible={isConfirmResetTeamsModalVisible}
        onConfirm={handleConfirmResetTeamsClick}
        onCancel={handleCancelResetTeamsClick}
      >
        לאפס את כל הנהגים והנוסעים שמשוייכים למסלולים?
      </ConfirmationModal>

      {isPromptMergeRoutesModalVisible && (
        <PromptModal
          title="איחוד מסלולים"
          inputLabel="שם המסלול החדש"
          onSaveClick={handleSaveMergeClick}
          onCancel={handleCancelMergeClick}
        />
      )}

      {isNotesModalVisible && (
        <NotesModal
          distributionId={distributionId}
          onNotesEdited={() => {
            refetchRoutesList();
            setNotesModalVisibility(false);
          }}
          onCancel={() => {
            setNotesModalVisibility(false);
          }}
          defaultValue={distribution?.notes}
        />
      )}

      <AlertModal
        isVisible={isAlertMergeRoutesModalVisible}
        onConfirm={handleConfirmMergeAlertClick}
      >
        יש לבחור לפחות 2 מסלולים לאיחוד
      </AlertModal>

      <AlertModal
        isVisible={isAlertActiveRouteSelectedVisible}
        onConfirm={() => setAlertActiveRouteSelectedVisibility(false)}
      >
        לא ניתן למחוק מסלולים פעילים. יש להשבית את המסלולים לפני המחיקה.
      </AlertModal>

      <ConfirmationModal
        title="מחיקת מסלולים"
        isVisible={isConfirmDeleteRoutesModalVisible}
        onConfirm={handleConfirmDeleteRoutesClick}
        onCancel={handleCancelDeleteRoutesClick}
      >
        בטוח שברצונך למחוק את המסלולים שנבחרו?
        <br />
        כל הכתובות שמשוייכות למסלולים אלו ימחקו גם כן.
      </ConfirmationModal>
    </>
  );
};
